import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent, identifyUser, setUserProperties } from '@/lib/posthog';
import { metaPixel } from '@/lib/metaPixel';
import { v4 as uuidv4 } from 'uuid';
import { getUTMParams } from '@/hooks/useUTMCapture';

// Events that should be persisted to Supabase for business analytics
const BUSINESS_EVENTS = [
  // Interview flow
  'interview_started',
  'interview_question_answered',
  'interview_completed',
  'interview_abandoned',
  
  // Resume analysis
  'resume_uploaded',
  'resume_analysis_completed',
  'resume_analysis_failed',
  
  // Job role selection
  'job_role_selected',
  'gap_analysis_viewed',
  
  // Premium/Conversion
  'premium_upgrade_prompt_shown',
  'premium_upgrade_clicked',
  'premium_upgrade_completed',
  'free_interview_consumed',
  
  // Cross-sell
  'cross_sell_impression',
  'cross_sell_clicked',
  
  // Resume Builder
  'resume_builder_started',
  'resume_builder_completed',
  'resume_downloaded',
  
  // Career DNA
  'career_assessment_started',
  'career_assessment_completed',
  
  // Job Hunt
  'job_hunt_started',
  'job_discovered',
  'job_applied',
  
  // Feedback Report
  'feedback_report_viewed',
  'readiness_score_generated',
  
  // Auth
  'user_signed_up',
  'user_signed_in',
  'user_signed_out',
];

// Event categories for organization
export const EventCategory = {
  INTERVIEW: 'interview',
  RESUME: 'resume',
  CONVERSION: 'conversion',
  CAREER: 'career',
  JOB_HUNT: 'job_hunt',
  AUTH: 'auth',
  NAVIGATION: 'navigation',
  ENGAGEMENT: 'engagement',
} as const;

type EventCategoryType = typeof EventCategory[keyof typeof EventCategory];

interface TrackingOptions {
  category?: EventCategoryType;
  persistToDb?: boolean;
  institutionId?: string;
}

export function useTracking() {
  const { user, profile } = useAuth();
  const sessionIdRef = useRef<string>(getOrCreateSessionId());
  
  // Identify user when they log in
  useEffect(() => {
    if (user?.id) {
      identifyUser(user.id, {
        email: user.email,
        full_name: profile?.full_name,
        subscription_tier: profile?.subscription_tier,
        institution_id: profile?.institution_id,
      });
      
      setUserProperties({
        subscription_tier: profile?.subscription_tier,
        institution_id: profile?.institution_id,
      });
    }
  }, [user?.id, user?.email, profile?.full_name, profile?.subscription_tier, profile?.institution_id]);

  const track = useCallback(async (
    eventType: string,
    eventData?: Record<string, any>,
    options?: TrackingOptions
  ) => {
    const { category, persistToDb, institutionId } = options || {};
    const pagePath = typeof window !== 'undefined' ? window.location.pathname : '';
    
    // Merge UTM params so every event carries campaign attribution
    const utmParams = getUTMParams();

    const enrichedData = {
      ...eventData,
      user_id: user?.id,
      institution_id: institutionId || profile?.institution_id,
      subscription_tier: profile?.subscription_tier,
      page_path: pagePath,
      session_id: sessionIdRef.current,
      timestamp: new Date().toISOString(),
      ...(utmParams || {}),
    };

    // Always send to PostHog for UX insights
    captureEvent(eventType, enrichedData);

    // Fire Meta Pixel events for key conversions
    fireMetaPixelEvent(eventType, eventData);

    // Persist to Supabase if it's a business event or explicitly requested
    const shouldPersist = persistToDb ?? BUSINESS_EVENTS.includes(eventType);
    
    if (shouldPersist) {
      try {
        await supabase.from('user_events').insert({
          user_id: user?.id || null,
          institution_id: institutionId || profile?.institution_id || null,
          event_type: eventType,
          event_category: category || inferCategory(eventType),
          event_data: { ...(eventData || {}), ...(utmParams || {}) },
          page_path: pagePath,
          session_id: sessionIdRef.current,
        });
      } catch (error) {
        console.error('Failed to persist event to Supabase:', error);
      }
    }
  }, [user?.id, profile?.institution_id, profile?.subscription_tier]);

  // Convenience methods for common events
  const trackPageView = useCallback((pageName: string, additionalData?: Record<string, any>) => {
    track('page_view', { page_name: pageName, ...additionalData }, { 
      category: EventCategory.NAVIGATION,
      persistToDb: false // Page views only to PostHog
    });
  }, [track]);

  const trackInterviewEvent = useCallback((
    event: 'started' | 'question_answered' | 'completed' | 'abandoned',
    data?: Record<string, any>
  ) => {
    track(`interview_${event}`, data, { category: EventCategory.INTERVIEW });
  }, [track]);

  const trackResumeEvent = useCallback((
    event: 'uploaded' | 'analysis_completed' | 'analysis_failed' | 'downloaded',
    data?: Record<string, any>
  ) => {
    const eventName = event === 'downloaded' ? 'resume_downloaded' : `resume_${event}`;
    track(eventName, data, { category: EventCategory.RESUME });
  }, [track]);

  const trackConversionEvent = useCallback((
    event: 'prompt_shown' | 'clicked' | 'completed',
    data?: Record<string, any>
  ) => {
    track(`premium_upgrade_${event}`, data, { category: EventCategory.CONVERSION });
  }, [track]);

  const trackCrossSell = useCallback((
    event: 'impression' | 'clicked',
    feature: string,
    data?: Record<string, any>
  ) => {
    track(`cross_sell_${event}`, { feature, ...data }, { category: EventCategory.CONVERSION });
  }, [track]);

  const trackCareerEvent = useCallback((
    event: 'assessment_started' | 'assessment_completed',
    data?: Record<string, any>
  ) => {
    track(`career_${event}`, data, { category: EventCategory.CAREER });
  }, [track]);

  const trackJobHuntEvent = useCallback((
    event: 'started' | 'discovered' | 'applied',
    data?: Record<string, any>
  ) => {
    track(event === 'started' ? 'job_hunt_started' : `job_${event}`, data, { 
      category: EventCategory.JOB_HUNT 
    });
  }, [track]);

  const trackAuthEvent = useCallback((
    event: 'signed_up' | 'signed_in' | 'signed_out',
    data?: Record<string, any>
  ) => {
    track(`user_${event}`, data, { category: EventCategory.AUTH });
  }, [track]);

  return {
    track,
    trackPageView,
    trackInterviewEvent,
    trackResumeEvent,
    trackConversionEvent,
    trackCrossSell,
    trackCareerEvent,
    trackJobHuntEvent,
    trackAuthEvent,
  };
}

// Helper to get or create session ID
function getOrCreateSessionId(): string {
  const STORAGE_KEY = 'careerfy_session_id';
  const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes
  
  if (typeof window === 'undefined') return uuidv4();
  
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const { id, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < SESSION_DURATION) {
        // Update timestamp to extend session
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, timestamp: Date.now() }));
        return id;
      }
    } catch {
      // Invalid stored value, create new
    }
  }
  
  const newId = uuidv4();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: newId, timestamp: Date.now() }));
  return newId;
}

// Infer category from event type
function inferCategory(eventType: string): EventCategoryType {
  if (eventType.startsWith('interview_')) return EventCategory.INTERVIEW;
  if (eventType.startsWith('resume_')) return EventCategory.RESUME;
  if (eventType.startsWith('premium_') || eventType.startsWith('cross_sell_')) return EventCategory.CONVERSION;
  if (eventType.startsWith('career_')) return EventCategory.CAREER;
  if (eventType.startsWith('job_')) return EventCategory.JOB_HUNT;
  if (eventType.startsWith('user_')) return EventCategory.AUTH;
  if (eventType.startsWith('page_')) return EventCategory.NAVIGATION;
  return EventCategory.ENGAGEMENT;
}

// Map business events to Meta Pixel events
function fireMetaPixelEvent(eventType: string, eventData?: Record<string, any>) {
  switch (eventType) {
    case 'user_signed_up':
      metaPixel.completeRegistration({ content_name: 'Careerfy Signup' });
      break;
    case 'resume_uploaded':
      metaPixel.resumeUploaded();
      metaPixel.lead({ content_name: 'Resume Upload', content_category: 'Interview' });
      metaPixel.submitApplication({ content_name: 'Resume Upload', content_category: 'Interview' });
      break;
    case 'interview_started':
      metaPixel.interviewStarted({ job_role: eventData?.job_role });
      metaPixel.submitApplication({ content_name: 'Interview Started', content_category: 'Interview' });
      break;
    case 'interview_completed':
      metaPixel.interviewCompleted({ job_role: eventData?.job_role, score: eventData?.score });
      break;
    case 'premium_upgrade_clicked':
      metaPixel.initiateCheckout({ value: eventData?.value, content_ids: [eventData?.plan_id] });
      metaPixel.addPaymentInfo({ value: eventData?.value, content_ids: [eventData?.plan_id] });
      break;
    case 'premium_upgrade_completed':
      metaPixel.purchase({ value: eventData?.value || 0, content_name: eventData?.plan_name });
      metaPixel.subscribe({ value: eventData?.value || 0, predicted_ltv: eventData?.predicted_ltv });
      break;
    case 'career_assessment_started':
      metaPixel.careerAssessmentStarted();
      break;
    case 'career_assessment_completed':
      metaPixel.careerAssessmentCompleted();
      break;
    case 'resume_builder_started':
      metaPixel.resumeBuilderStarted();
      metaPixel.customizeProduct({ content_name: 'Resume Builder', content_category: 'Resume' });
      break;
    case 'resume_downloaded':
      metaPixel.resumeDownloaded();
      break;
    case 'feedback_report_viewed':
      metaPixel.viewContent({ content_name: 'Feedback Report', content_category: 'Interview' });
      break;
  }
}
